/**
 * SectorBreakdownModal Component
 *
 * Displays a detailed sector breakdown table similar to Vanguard's interface.
 * Shows sector allocation percentages for each investment and portfolio totals.
 * Data is based on user-entered sector information from fund factsheets.
 */

import React, { useMemo } from 'react';
import { X, AlertCircle, Info, ExternalLink } from 'lucide-react';
import { useCountry } from '../../CountryContext';
import { formatCurrency as formatCurrencyConfig } from '../../countryConfig';
import { SECTORS, SECTOR_COLORS, calculatePortfolioSectorAllocation } from '../../utils/sectorData';

const SectorBreakdownModal = ({
  isOpen,
  onClose,
  investments = [],
  isDark = false
}) => {
  const { selectedCountry } = useCountry();

  // Calculate sector allocation for all investments
  const sectorData = useMemo(() => {
    return calculatePortfolioSectorAllocation(investments);
  }, [investments]);

  // Sectors to display (filter out zeros in totals)
  const activeSectors = useMemo(() => {
    return SECTORS.filter(sector => sectorData.percentages[sector] > 0.01);
  }, [sectorData]);

  const formatValue = (value) => {
    return formatCurrencyConfig(value, selectedCountry);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    if (value === 0) return '-';
    return `${value.toFixed(2)}%`;
  };

  if (!isOpen) return null;

  // Check if any investments have sector data
  const hasAnySectorData = sectorData.coveragePercent > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        className={`w-full max-w-7xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-2xl font-bold">Sectors</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {hasAnySectorData ? (
          <>
            {/* Table */}
            <div className="overflow-auto max-h-[calc(90vh-200px)]">
              <table className="w-full min-w-[1000px]">
                <thead className={`sticky top-0 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <th className={`px-4 py-3 text-left text-sm font-semibold min-w-[250px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Investments
                    </th>
                    {activeSectors.map(sector => (
                      <th
                        key={sector}
                        className={`px-3 py-3 text-center text-xs font-semibold min-w-[90px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {sector.split(' ').map((word, i) => (
                          <span key={i}>
                            {word}
                            {i < sector.split(' ').length - 1 && <br />}
                          </span>
                        ))}
                      </th>
                    ))}
                    <th className={`px-3 py-3 text-center text-xs font-semibold min-w-[80px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      No Data
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-semibold min-w-[80px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sectorData.investments.map((investment, idx) => {
                    const portfolioPercentage = sectorData.totalPortfolioValue > 0
                      ? (investment.value / sectorData.totalPortfolioValue) * 100
                      : 0;

                    const hasSectorData = investment.hasSectorData;
                    const category = (investment.category || '').toLowerCase();
                    const isNonSectorAsset = ['cash', 'cryptocurrency', 'crypto', 'bonds'].includes(category);

                    return (
                      <tr
                        key={investment.name + idx}
                        className={`border-b ${isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}
                      >
                        <td className={`px-4 py-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          <div>
                            <span className="font-medium">{investment.name}</span>
                            {investment.ticker && (
                              <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                ({investment.ticker})
                              </span>
                            )}
                            {!hasSectorData && !isNonSectorAsset && (
                              <span className={`ml-2 text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                (needs data)
                              </span>
                            )}
                          </div>
                        </td>

                        {activeSectors.map(sector => {
                          if (!hasSectorData || isNonSectorAsset) {
                            return (
                              <td
                                key={sector}
                                className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                              >
                                -
                              </td>
                            );
                          }

                          const investmentSectorPercent = investment.sectors?.[sector] || 0;
                          const portfolioContribution = (portfolioPercentage * investmentSectorPercent) / 100;

                          return (
                            <td
                              key={sector}
                              className={`px-3 py-3 text-center text-sm ${
                                portfolioContribution > 0.01
                                  ? isDark ? 'text-gray-200' : 'text-gray-800'
                                  : isDark ? 'text-gray-500' : 'text-gray-400'
                              }`}
                            >
                              {portfolioContribution > 0.01 ? `${portfolioContribution.toFixed(2)}%` : '-'}
                            </td>
                          );
                        })}

                        <td className={`px-3 py-3 text-center text-sm`}>
                          {!hasSectorData ? (
                            <span className={isDark ? 'text-yellow-400' : 'text-yellow-600'}>
                              {formatPercent(portfolioPercentage)}
                            </span>
                          ) : (
                            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                          )}
                        </td>

                        <td className={`px-4 py-3 text-right text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {formatPercent(portfolioPercentage)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Total row */}
                  <tr className={`${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} font-semibold`}>
                    <td className={`px-4 py-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      Total
                    </td>
                    {activeSectors.map(sector => (
                      <td
                        key={sector}
                        className={`px-3 py-4 text-center text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}
                      >
                        {formatPercent(sectorData.percentages[sector])}
                      </td>
                    ))}
                    <td className={`px-3 py-4 text-center text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      {formatPercent(100 - sectorData.coveragePercent)}
                    </td>
                    <td className={`px-4 py-4 text-right text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      100%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer disclaimer */}
            <div className={`p-4 border-t ${isDark ? 'border-gray-700 bg-blue-900/20' : 'border-gray-200 bg-blue-50'}`}>
              <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                Please note Insight totals may not always equal 100% due to rounding.
                Sector data is based on information you enter from your fund factsheets.
                Investments marked "(needs data)" require you to add sector allocation information.
              </p>
            </div>
          </>
        ) : (
          /* No data state */
          <div className="p-8">
            <div className={`text-center py-12 px-6 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
              <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                No Sector Data Available
              </h3>
              <p className={`text-sm mb-6 max-w-lg mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                To see sector allocation breakdown, you need to add sector data to your investments.
                This information can be found in your fund factsheets.
              </p>

              <div className={`p-4 rounded-lg text-left max-w-md mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-start gap-3">
                  <Info className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div>
                    <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      Where to find sector information:
                    </p>
                    <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <li>• <strong>Fund Factsheet</strong> - "Portfolio Breakdown" section</li>
                      <li>• <strong>Fund Provider Website</strong> - "Holdings" or "Composition"</li>
                      <li>• <strong>Morningstar</strong> - Search fund → "Portfolio" tab</li>
                      <li>• <strong>KIID Document</strong> - Key Investor Information</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className={`text-xs mt-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Edit each investment to add its sector allocation data
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectorBreakdownModal;
